// ABOUTME: Editable table section for reports with TableEditorModal integration.
// ABOUTME: Displays tables in view mode with instant optimistic updates on edit.

import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { GripVertical, Trash2, Pencil } from 'lucide-react';
import { ReportTable } from './ReportTable';
import { TableEditorModal } from './TableEditorModal';
import type { TableData } from '@/types/api';

interface TableContent {
  title: string;
  headers: string[];
  rows: (string | number)[][];
}

interface TableSectionProps {
  content?: string;
  tableData?: TableData;
  onUpdate: (content: string) => void;
  onDelete: () => void;
  onTableSave?: (data: TableContent) => Promise<{ version: number }>;
  isDragging?: boolean;
  dragHandleProps?: Record<string, unknown>;
}

const DEFAULT_TABLE_DATA: TableContent = {
  title: 'Table Title',
  headers: ['Column 1', 'Column 2', 'Column 3'],
  rows: [
    ['Row 1 Data', 100, 'Value'],
    ['Row 2 Data', 200, 'Value'],
    ['Row 3 Data', 300, 'Value'],
  ],
};

export function TableSection({
  content,
  tableData,
  onUpdate,
  onDelete,
  onTableSave,
  isDragging,
  dragHandleProps,
}: TableSectionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [localData, setLocalData] = useState<TableContent | null>(null);

  const parseContent = useCallback((): TableContent => {
    if (localData) {
      return localData;
    }

    if (tableData) {
      const headers = (tableData.columns || []).map((col) =>
        col.header || col.key || col.id || String(col)
      );
      const columnIds = (tableData.columns || []).map((col) =>
        col.id || col.key || col.header || ''
      );
      const rows = (tableData.rows || []).map((row) => {
        if (Array.isArray(row)) {
          return row.map((cell) => (cell !== undefined && cell !== null ? cell : ''));
        }
        return columnIds.map((colId) => {
          const value = row[colId] ?? '';
          return value !== undefined && value !== null ? value : '';
        });
      }) as (string | number)[][];

      return {
        title: tableData.title || 'Table',
        headers: headers.length > 0 ? headers : DEFAULT_TABLE_DATA.headers,
        rows: rows.length > 0 ? rows : DEFAULT_TABLE_DATA.rows,
      };
    }

    if (content) {
      try {
        const parsed = JSON.parse(content);
        return {
          title: parsed.title || 'Table',
          headers: parsed.headers || DEFAULT_TABLE_DATA.headers,
          rows: parsed.rows || DEFAULT_TABLE_DATA.rows,
        };
      } catch {
        return DEFAULT_TABLE_DATA;
      }
    }

    return DEFAULT_TABLE_DATA;
  }, [content, tableData, localData]);

  const tableContent = parseContent();

  const handleSave = useCallback(async (newData: TableContent) => {
    if (onTableSave) {
      await onTableSave(newData);
    }
    setLocalData(newData);
    onUpdate(JSON.stringify(newData));
  }, [onUpdate, onTableSave]);

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={`group relative ${isDragging ? 'opacity-50' : ''}`}
      >
        {/* Drag Handle */}
        <div className="absolute -left-10 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            {...dragHandleProps}
            className="p-1 text-surface-400 hover:text-surface-600 cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="w-4 h-4" />
          </button>
        </div>

        {/* Table Content */}
        <div
          className="relative rounded-sm bg-surface-0 border border-surface-200 p-4 hover:border-surface-300 transition-colors cursor-pointer"
          onClick={() => setIsEditing(true)}
        >
          {/* Inline Edit/Delete Controls */}
          <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
              className="p-1.5 rounded-sm bg-surface-50 text-surface-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
              title="Edit"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-1.5 rounded-sm bg-surface-50 text-surface-500 hover:text-error hover:bg-error-light transition-colors cursor-pointer"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {tableContent.title && (
            <h3 className="text-sm font-medium text-surface-600 mb-3">
              {tableContent.title}
            </h3>
          )}
          <ReportTable
            data={{ headers: tableContent.headers, rows: tableContent.rows }}
          />
        </div>
      </motion.div>

      {/* Table Editor Panel */}
      <TableEditorModal
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        initialData={tableContent}
        onSave={handleSave}
      />
    </>
  );
}
